var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { json } from "@hyper-hyper-space/hhs3_json";
import { formatPredicate, } from "@hyper-hyper-space/hhs3_rdb";
function isFullProfile(options) {
    return (options === null || options === void 0 ? void 0 : options.profile) !== 'schema';
}
function useAliases(options) {
    return (options === null || options === void 0 ? void 0 : options.aliasMode) === true && options.aliases !== undefined;
}
function useComments(options) {
    return (options === null || options === void 0 ? void 0 : options.comments) !== false;
}
function schemaVersionScope(options) {
    var _a;
    if ((options === null || options === void 0 ? void 0 : options.schemaRef) === undefined)
        return undefined;
    return { objectId: options.schemaRef, objectName: (_a = options.schemaName) !== null && _a !== void 0 ? _a : 'schema' };
}
function renderObjectRef(scope, id, hint, options) {
    var _a, _b, _c, _d, _e;
    if (useAliases(options) && isFullProfile(options)) {
        const aliases = options.aliases;
        return scope === 'schema' ? aliases.schema(id, hint) : aliases.group(id, hint);
    }
    if (isFullProfile(options))
        return `#${id}`;
    if (scope === 'schema')
        return (_b = (_a = options === null || options === void 0 ? void 0 : options.resolveSchemaName) === null || _a === void 0 ? void 0 : _a.call(options, id)) !== null && _b !== void 0 ? _b : `#${id}`;
    return (_e = (_d = (_c = options === null || options === void 0 ? void 0 : options.resolveGroupName) === null || _c === void 0 ? void 0 : _c.call(options, id)) !== null && _d !== void 0 ? _d : hint) !== null && _e !== void 0 ? _e : `#${id}`;
}
function renderGroupTarget(options) {
    if ((options === null || options === void 0 ? void 0 : options.groupRef) === undefined)
        return '<group>';
    return renderObjectRef('group', options.groupRef, options.groupName, options);
}
export function renderCreateDatabase(payload, options) {
    var _a, _b;
    const creators = renderCreators((_a = payload.creators) !== null && _a !== void 0 ? _a : [], options);
    const seed = isFullProfile(options) && payload.seed !== undefined && payload.seed.length > 0
        ? ` SEED ${sqlString(payload.seed)}`
        : '';
    return `CREATE DATABASE ${(_b = payload.name) !== null && _b !== void 0 ? _b : payload.seed}${seed}${creators};`;
}
export function renderCreateSchema(payload, options) {
    const creators = renderCreators(payload.creators, options);
    const tables = payload.tables.map(renderTableDef).join(',\n  ');
    return `CREATE SCHEMA ${payload.name}${creators} AS (\n  ${tables}\n);`;
}
export function renderCreateTableGroup(payload, options) {
    var _a, _b, _c, _d, _e, _f;
    const schemaComment = useComments(options) && (options === null || options === void 0 ? void 0 : options.schemaName) !== undefined && !isFullProfile(options)
        ? `-- ${options.schemaName}\n`
        : '';
    const seed = isFullProfile(options) && payload.seed !== undefined && payload.seed.length > 0
        ? ` SEED ${sqlString(payload.seed)}`
        : '';
    const schemaRef = renderObjectRef('schema', payload.schemaRef, (_a = options === null || options === void 0 ? void 0 : options.schemaName) !== null && _a !== void 0 ? _a : (_b = options === null || options === void 0 ? void 0 : options.resolveSchemaName) === null || _b === void 0 ? void 0 : _b.call(options, payload.schemaRef), options);
    const parts = [
        `${schemaComment}CREATE TABLEGROUP ${payload.name}${seed} USING SCHEMA ${schemaRef} AT ${renderVersionSet(payload.schemaVersion, schemaVersionScope(options), options)}`,
    ];
    for (const [name, id] of Object.entries((_c = payload.bindings) !== null && _c !== void 0 ? _c : {})) {
        if (isFullProfile(options)) {
            parts.push(`BIND ${name} => #${id}`);
        }
        else {
            const groupName = (_e = (_d = options === null || options === void 0 ? void 0 : options.resolveGroupName) === null || _d === void 0 ? void 0 : _d.call(options, id)) !== null && _e !== void 0 ? _e : name;
            parts.push(`BIND ${name} => ${groupName}`);
        }
    }
    if (payload.idProvider !== undefined)
        parts.push(`USING IDENTITIES ${payload.idProvider}`);
    if (payload.canDeploy !== undefined)
        parts.push(`ALLOW UPDATE SCHEMA IF ${formatPredicate(payload.canDeploy)}`);
    for (const [binding, pred] of Object.entries((_f = payload.canObserve) !== null && _f !== void 0 ? _f : {})) {
        parts.push(`ALLOW UPDATE REF ${binding} IF ${formatPredicate(pred)}`);
    }
    if (payload.initialRows !== undefined) {
        const rows = [];
        for (const [table, tableRows] of Object.entries(payload.initialRows)) {
            for (const row of tableRows) {
                rows.push(renderInitialRow(table, row, options));
            }
        }
        if (rows.length > 0)
            parts.push(`WITH ROWS (\n  ${rows.join(',\n  ')}\n)`);
    }
    return `${parts.join('\n  ')};`;
}
export function renderAddSchema(payload, options) {
    var _a, _b;
    const target = renderObjectRef('schema', payload.schemaId, (_a = options === null || options === void 0 ? void 0 : options.resolveSchemaName) === null || _a === void 0 ? void 0 : _a.call(options, payload.schemaId), options);
    const db = (_b = options === null || options === void 0 ? void 0 : options.databaseName) !== null && _b !== void 0 ? _b : '<database>';
    return `ADD SCHEMA ${target} TO ${db}${renderNote(payload.note)}${renderBy(payload.author, options)}${isFullProfile(options) ? renderAt(options) : ''};`;
}
export function renderAddGroup(payload, options) {
    var _a, _b;
    const target = renderObjectRef('group', payload.groupId, (_a = options === null || options === void 0 ? void 0 : options.resolveGroupName) === null || _a === void 0 ? void 0 : _a.call(options, payload.groupId), options);
    const db = (_b = options === null || options === void 0 ? void 0 : options.databaseName) !== null && _b !== void 0 ? _b : '<database>';
    return `ADD TABLEGROUP ${target} TO ${db}${renderNote(payload.note)}${renderBy(payload.author, options)}${isFullProfile(options) ? renderAt(options) : ''};`;
}
export function renderSchemaUpdate(payload, options) {
    var _a;
    const rules = payload.migration.map(renderMigrationRule).join(',\n  ');
    const schemaRef = (_a = options === null || options === void 0 ? void 0 : options.schemaRef) !== null && _a !== void 0 ? _a : 'unknown';
    const comment = useComments(options) && (options === null || options === void 0 ? void 0 : options.schemaName) !== undefined ? `-- ${options.schemaName}\n` : '';
    const schemaTarget = schemaRef === 'unknown'
        ? schemaRef
        : (useAliases(options) && isFullProfile(options)
            ? renderObjectRef('schema', schemaRef, options === null || options === void 0 ? void 0 : options.schemaName, options)
            : `#${schemaRef}`);
    return `${comment}ALTER SCHEMA ${schemaTarget} AS (\n  ${rules}\n)${renderBy(payload.author, options)}${renderAt(options)};`;
}
export function renderRowOp(payload, table, options) {
    const target = table !== null && table !== void 0 ? table : '<table>';
    if (payload.action === 'insert') {
        const insert = payload;
        const cols = Object.keys(insert.values);
        const vals = cols.map((c) => renderLiteral(insert.values[c]));
        if (isFullProfile(options) && insert.uuid !== undefined) {
            return `INSERT INTO ${target} (uuid, ${cols.join(', ')}) VALUES (${sqlString(insert.uuid)}, ${vals.join(', ')})${renderBy(insert.author, options)}${renderAt(options)};`;
        }
        return `INSERT INTO ${target} (${cols.join(', ')}) VALUES (${vals.join(', ')})${renderBy(insert.author, options)}${renderAt(options)};`;
    }
    if (payload.action === 'update') {
        const update = payload;
        const values = Object.entries(update.values).map(([k, v]) => `${k} = ${renderLiteral(v)}`).join(', ');
        return `UPDATE ${target} SET ${values} WHERE rowId = #${update.rowId}${renderBy(update.author, options)}${renderAt(options)};`;
    }
    return `DELETE FROM ${target} WHERE rowId = #${payload.rowId}${renderBy(payload.author, options)}${renderAt(options)};`;
}
export function renderRefOp(payload, options) {
    var _a, _b, _c, _d;
    const author = payload.author;
    const trailing = `${renderBy(author, options)}${renderAt(options)}`;
    const isSchemaDeploy = (options === null || options === void 0 ? void 0 : options.schemaRef) !== undefined && payload.refId === options.schemaRef;
    const refVersionScope = (_a = options === null || options === void 0 ? void 0 : options.refVersionScope) !== null && _a !== void 0 ? _a : (isSchemaDeploy
        ? schemaVersionScope(options)
        : {
            objectId: payload.refId,
            objectName: (_c = (_b = options === null || options === void 0 ? void 0 : options.resolveGroupName) === null || _b === void 0 ? void 0 : _b.call(options, payload.refId)) !== null && _c !== void 0 ? _c : 'group',
        });
    const version = renderVersionSet(payload.refVersion, refVersionScope, options);
    const group = renderGroupTarget(options);
    const refTarget = isSchemaDeploy
        ? renderObjectRef('schema', payload.refId, options === null || options === void 0 ? void 0 : options.schemaName, options)
        : renderObjectRef('group', payload.refId, (_d = options === null || options === void 0 ? void 0 : options.resolveGroupName) === null || _d === void 0 ? void 0 : _d.call(options, payload.refId), options);
    if (isSchemaDeploy) {
        return `UPDATE SCHEMA ${refTarget} TO ${version} ON ${group}${trailing};`;
    }
    return `UPDATE REF ${refTarget} TO ${version} ON ${group}${trailing};`;
}
export function renderBundle(payload, options) {
    const writes = payload.writes.map((w) => {
        const op = w.op;
        const _a = op, { author: _author } = _a, innerOp = __rest(_a, ["author"]);
        return renderRowOp(innerOp, w.table, options);
    }).join('\n  ');
    const author = payload.author;
    return `BUNDLE ON ${renderGroupTarget(options)} (\n  ${writes}\n)${renderBy(author, options)}${renderAt(options)};`;
}
function isRowOpAction(action) {
    return action === 'insert' || action === 'update' || action === 'delete';
}
function renderTableScopePayload(payload, tableName, options) {
    const action = payload['action'];
    if (isRowOpAction(action)) {
        return renderRowOp(payload, tableName, options);
    }
    if (action === 'rows' && Array.isArray(payload['ops'])) {
        return payload['ops']
            .map((op) => renderRowOp(op, tableName, options))
            .join('\n');
    }
    return undefined;
}
export function renderOp(payload, options) {
    if (!isObject(payload))
        return `-- unknown payload ${json.toStringNormalized(payload)}`;
    if (payload['action'] === 'create' && payload['type'] === 'hhs/rdb_v1') {
        return renderCreateDatabase(payload, options);
    }
    if (payload['action'] === 'create' && payload['type'] === 'hhs/rschema_v1') {
        return renderCreateSchema(payload, options);
    }
    if (payload['action'] === 'create' && payload['type'] === 'hhs/rtable_group_v1') {
        return renderCreateTableGroup(payload, options);
    }
    if (payload['action'] === 'add-schema')
        return renderAddSchema(payload, options);
    if (payload['action'] === 'add-group')
        return renderAddGroup(payload, options);
    if (payload['action'] === 'schema-update')
        return renderSchemaUpdate(payload, options);
    if (payload['action'] === 'row') {
        const row = payload;
        return renderRowOp(row.op, row.table, options);
    }
    if (payload['action'] === 'bundle')
        return renderBundle(payload, options);
    if (payload['action'] === 'ref-advance')
        return renderRefOp(payload, options);
    if ((options === null || options === void 0 ? void 0 : options.tableName) !== undefined) {
        const rendered = renderTableScopePayload(payload, options.tableName, options);
        if (rendered !== undefined)
            return rendered;
    }
    return `-- unknown payload ${json.toStringNormalized(payload)}`;
}
function renderTableDef(table) {
    var _a;
    const colIndent = '    ';
    const colLines = Object.entries(table.columns)
        .map(([name, def]) => { var _a; return `${colIndent}${renderColumnDef(name, def, (_a = table.fks) === null || _a === void 0 ? void 0 : _a[name])}`; });
    const cols = colLines.length === 0 ? '' : `\n${colLines.join(',\n')}\n  `;
    const structural = [];
    if (table.concurrentDeletes !== undefined) {
        structural.push(table.concurrentDeletes ? 'CONCURRENT DELETES' : 'NO CONCURRENT DELETES');
    }
    if (table.idProvider !== undefined) {
        const provider = table.idProvider.keyIdColumn === 'keyId' && table.idProvider.publicKeyColumn === 'publicKey'
            ? 'IDENTITY PROVIDER'
            : `IDENTITY PROVIDER (${table.idProvider.keyIdColumn}, ${table.idProvider.publicKeyColumn})`;
        structural.push(provider);
    }
    const allows = ((_a = table.restrictions) !== null && _a !== void 0 ? _a : [])
        .map((r) => `ALLOW ${r.on} IF ${formatPredicate(r.rule, { gatedTable: table.name })}`);
    let suffix = '';
    if (structural.length > 0) {
        suffix = ` ${structural.join(' ')}`;
    }
    if (allows.length > 0) {
        suffix += allows.map((allow) => `\n    ${allow}`).join('');
    }
    return `TABLE ${table.name} (${cols})${suffix}`;
}
function renderColumnDef(name, def, fk) {
    const parts = [name, def.type];
    if (def.nullable)
        parts.push('NULL');
    if (def.default !== undefined)
        parts.push(`DEFAULT ${renderLiteral(def.default)}`);
    if (def.pub)
        parts.push('PUB');
    if (def.readonly)
        parts.push('READONLY');
    if (fk !== undefined)
        parts.push(`REFERENCES ${fk}`);
    return parts.join(' ');
}
function renderMigrationRule(rule) {
    switch (rule.rule) {
        case 'add-table':
            return `ADD TABLE ${renderTableDef(rule.def).replace(/^TABLE /, '')}`;
        case 'drop-table':
            return `DROP TABLE ${rule.table}`;
        case 'add-column':
            return `ADD COLUMN ${rule.table}.${renderColumnDef(rule.column, rule.def)}`;
        case 'drop-column':
            return `DROP COLUMN ${rule.table}.${rule.column}`;
        case 'set-concurrent-deletes':
            return `SET CONCURRENT DELETES ${rule.table} ${String(rule.value)}`;
        case 'set-fks':
            return `SET FKS ${rule.table} (${Object.entries(rule.fks).map(([c, r]) => `${c} REFERENCES ${r}`).join(', ')})`;
        case 'set-restrictions':
            return `SET ALLOW RULES ${rule.table} (\n    ${rule.restrictions.map((r) => `ALLOW ${r.on} IF ${formatPredicate(r.rule, { gatedTable: rule.table })}`).join(',\n    ')}\n  )`;
    }
}
function renderInitialRow(table, row, options) {
    const parts = Object.entries(row.values).map(([k, v]) => renderRowValue(k, v, options));
    if (isFullProfile(options) && row.uuid !== undefined) {
        parts.unshift(`uuid=${sqlString(row.uuid)}`);
    }
    return `${table} (${parts.join(', ')})`;
}
function renderRowValue(column, value, options) {
    if (!useAliases(options) || typeof value !== 'string') {
        return `${column}=${renderLiteral(value)}`;
    }
    const aliases = options.aliases;
    if (column === 'publicKey' && aliases.lookupPublicKeyAlias !== undefined) {
        const name = aliases.lookupPublicKeyAlias(value);
        if (name !== undefined)
            return `${column}=publicKey($${name})`;
    }
    if (aliases.lookupKeyAlias !== undefined) {
        const name = aliases.lookupKeyAlias(value);
        if (name !== undefined)
            return `${column}=$${name}`;
    }
    return `${column}=${renderLiteral(value)}`;
}
function renderLiteral(value) {
    if (typeof value === 'string')
        return sqlString(value);
    return json.toStringNormalized(value);
}
function sqlString(value) {
    return `'${value.replace(/'/g, "''")}'`;
}
function renderNote(note) {
    return note === undefined ? '' : ` NOTE ${sqlString(note)}`;
}
function renderAt(options) {
    return (options === null || options === void 0 ? void 0 : options.at) === undefined ? '' : ` AT ${renderVersionSet(options.at, options.versionScope, options)}`;
}
function renderBy(author, options) {
    if (author === undefined)
        return '';
    if (useAliases(options))
        return ` BY $${options.aliases.key(author)}`;
    return ` BY #${author}`;
}
function renderCreators(creators, options) {
    if (creators.length === 0)
        return '';
    const names = creators.map((c) => {
        if (useAliases(options))
            return `$${options.aliases.key(c.keyId)}`;
        return sqlString(c.keyId);
    });
    return ` CREATORS (${names.join(', ')})`;
}
function renderVersionSet(set, scope, options) {
    if (useAliases(options) && scope !== undefined) {
        const names = [...json.fromSet(set)].map((h) => options.aliases.version(h, scope));
        return `{${names.join(', ')}}`;
    }
    return `{${[...json.fromSet(set)].map((h) => `#${h}`).join(', ')}}`;
}
function isObject(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
